import type { SupportedLocale } from './locales'

/**
 * Customer-facing "decorative item, not a toy" notice shown next to ordering
 * and confirmation in every customer surface. The binding wording lives in the
 * Terms (dashboard Terms of Service and each customer product's Terms); this
 * short notice points at it and must stay consistent with it.
 *
 * Children may paint an item with an adult supervising (founder decision
 * 2026-11-04: parents order Doodles for children to paint). The notice states
 * display use and the under-3 small-parts warning; it never claims any
 * toy-safety conformity.
 */
const title: Record<SupportedLocale, string> = {
  en: 'Not a toy',
  es: 'No es un juguete',
  fr: 'Ce n’est pas un jouet',
  de: 'Kein Spielzeug',
  pt: 'Não é um brinquedo',
  'zh-Hans': '非玩具',
  ja: 'おもちゃではありません',
  ko: '장난감이 아닙니다',
  ar: 'ليست لعبة',
  hi: 'खिलौना नहीं',
  ms: 'Bukan mainan',
}

const notice: Record<SupportedLocale, string> = {
  en: 'This is a decorative 3D-printed figurine or model for display, not a toy. It is not made or tested for play. Children may paint it with an adult supervising. Small parts can break off and are a choking hazard, so keep it away from children under 3.',
  es: 'Es una figura o maqueta decorativa impresa en 3D para exhibir, no un juguete. No está fabricada ni probada para jugar. Los niños pueden pintarla bajo la supervisión de un adulto. Las piezas pequeñas pueden desprenderse y provocar asfixia, así que mantenla fuera del alcance de menores de 3 años.',
  fr: 'Il s’agit d’une figurine ou d’un modèle décoratif imprimé en 3D, destiné à être exposé, et non d’un jouet. Il n’est ni conçu ni testé pour le jeu. Les enfants peuvent le peindre sous la surveillance d’un adulte. De petites pièces peuvent se détacher et présenter un risque d’étouffement : tenez-le hors de portée des enfants de moins de 3 ans.',
  de: 'Dies ist eine dekorative 3D-gedruckte Figur oder ein Modell zum Ausstellen, kein Spielzeug. Es ist nicht zum Spielen hergestellt oder geprüft. Kinder dürfen es unter Aufsicht eines Erwachsenen bemalen. Kleinteile können abbrechen und verschluckt werden (Erstickungsgefahr), daher von Kindern unter 3 Jahren fernhalten.',
  pt: 'Esta é uma figura ou maquete decorativa impressa em 3D para exposição, não um brinquedo. Não foi feita nem testada para brincar. As crianças podem pintá-la com a supervisão de um adulto. Peças pequenas podem soltar-se e causar asfixia, por isso mantenha-a fora do alcance de crianças com menos de 3 anos.',
  'zh-Hans': '这是用于陈列的 3D 打印装饰摆件或模型，并非玩具。它并非为玩耍而制造或测试。儿童可在成人看护下为其上色。小部件可能脱落并导致窒息，请放在 3 岁以下儿童接触不到的地方。',
  ja: 'これは飾って楽しむための 3D プリントの装飾用フィギュアまたは模型で、おもちゃではありません。遊び用として製造・試験されていません。お子さまが色を塗る際は、大人が付き添ってください。小さな部品が外れて窒息の原因となるおそれがあるため、3 歳未満のお子さまの手の届かない場所に置いてください。',
  ko: '전시용 3D 프린팅 장식 피규어 또는 모형이며 장난감이 아닙니다. 놀이용으로 제작되거나 시험되지 않았습니다. 어린이는 성인의 감독 아래 색칠할 수 있습니다. 작은 부품이 떨어져 질식 위험이 있으므로 3세 미만 어린이의 손이 닿지 않는 곳에 두세요.',
  ar: 'هذا مجسّم أو نموذج زخرفي مطبوع بتقنية ثلاثية الأبعاد للعرض، وليس لعبة. لم يُصنع أو يُختبر للعب. يمكن للأطفال تلوينه تحت إشراف شخص بالغ. قد تنفصل قطع صغيرة وتسبّب خطر الاختناق، لذا أبقِه بعيدًا عن متناول الأطفال دون 3 سنوات.',
  hi: 'यह प्रदर्शन के लिए 3D-प्रिंटेड सजावटी मूर्ति या मॉडल है, खिलौना नहीं। इसे खेलने के लिए बनाया या परखा नहीं गया है। बच्चे किसी वयस्क की निगरानी में इसे रंग सकते हैं। छोटे हिस्से टूटकर दम घुटने का खतरा पैदा कर सकते हैं, इसलिए इसे 3 वर्ष से कम उम्र के बच्चों की पहुँच से दूर रखें।',
  ms: 'Ini ialah patung atau model hiasan cetakan 3D untuk dipamerkan, bukan mainan. Ia tidak dibuat atau diuji untuk bermain. Kanak-kanak boleh mewarnakannya dengan pengawasan orang dewasa. Bahagian kecil boleh tertanggal dan menyebabkan risiko tercekik, jadi jauhkan daripada kanak-kanak bawah 3 tahun.',
}

export function decorativeItemNoticeTitle(locale: SupportedLocale): string {
  return title[locale]
}

export function decorativeItemNotice(locale: SupportedLocale): string {
  return notice[locale]
}
